import React, { useState, useEffect } from "react";
import ReactDom from "react-dom";
import { toast } from "react-toastify";
import styles from "./joinTablesModal.module.css";
import CloseIcon from "../../../assets/icons/circle-xmark.svg";

const JoinTablesModal = ({ 
  onClose, 
  onApply,
  onClear,
  joinTables,
  joinFieldsByTable,
  defaultJoinFields,
  joinRelationships,
  currentJoinConfig
}) => {
  const [selectedTable, setSelectedTable] = useState(currentJoinConfig?.table || "");
  const [joinType, setJoinType] = useState(currentJoinConfig?.type || "INNER");
  const [additionalFields, setAdditionalFields] = useState(
    currentJoinConfig?.selectFields?.filter(field => 
      !defaultJoinFields[currentJoinConfig?.table]?.includes(field)
    ) || []
  );

  useEffect(() => {
    if (currentJoinConfig?.table) {
      setSelectedTable(currentJoinConfig.table);
      setJoinType(currentJoinConfig.type);
      const defaultFields = defaultJoinFields[currentJoinConfig.table] || [];
      const additional = currentJoinConfig.selectFields?.filter(field => 
        !defaultFields.includes(field)
      ) || [];
      setAdditionalFields(additional);
    }
  }, [currentJoinConfig, defaultJoinFields]);

  const handleTableChange = (table) => {
    setSelectedTable(table);
    setAdditionalFields([]);
  };

  const handleFieldToggle = (fieldValue) => {
    setAdditionalFields(prev => {
      const isSelected = prev.includes(fieldValue);
      return isSelected
        ? prev.filter(f => f !== fieldValue)
        : [...prev, fieldValue];
    });
  };

  const handleApply = () => {
    if (!selectedTable) {
      toast.warning("Selecione uma tabela para mesclar");
      return;
    }

    const defaultFields = defaultJoinFields[selectedTable] || [];
    const allFields = [...defaultFields, ...additionalFields];

    if (allFields.length === 0) {
      toast.warning("Selecione pelo menos um campo");
      return;
    }

    const relationship = joinRelationships[selectedTable] || { 
      leftKey: 'StudentId', 
      rightKey: 'StudentId' 
    };

    const joinConfig = {
      enabled: true,
      table: selectedTable,
      type: joinType,
      leftKey: relationship.leftKey,
      rightKey: relationship.rightKey,
      on: relationship.rightKey,
      selectFields: allFields
    };

    onApply(joinConfig);
    onClose();
  };

  const handleClear = () => {
    onClear();
    onClose();
  };

  const getDefaultFields = () => {
    if (!selectedTable) return [];
    return defaultJoinFields[selectedTable] || [];
  };

  const getAdditionalFieldOptions = () => {
    if (!selectedTable) return [];
    const defaultFields = defaultJoinFields[selectedTable] || [];
    const allFields = joinFieldsByTable[selectedTable] || [];
    return allFields.filter(field => !defaultFields.includes(field.value));
  };

  const getRelationshipLabel = () => {
    if (!selectedTable) return "";
    return joinRelationships[selectedTable]?.label || "ID do Aluno";
  };

  const modalContent = (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Configurações de Exibição</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <img src={CloseIcon} alt="Fechar" />
          </button>
        </div>

        {/* Corpo do Modal */}
        <div className={styles.modalBody}>
          <h3 className={styles.sectionTitle}>Mesclar Tabelas</h3>
          <p className={styles.sectionDescription}>
            Selecione uma tabela para combinar dados
          </p>

          {/* Selects principais */}
          <div className={styles.selectsRow}>
            <div className={styles.selectField}>
              <label className={styles.selectLabel}>Selecione a tabela</label>
              <select
                value={selectedTable}
                onChange={(e) => handleTableChange(e.target.value)}
                className={styles.select}
              >
                <option value="">Selecione a tabela</option>
                {joinTables.filter(t => t.value !== "").map(table => (
                  <option key={table.value} value={table.value}>
                    {table.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.selectField}>
              <label className={styles.selectLabel}>Tipo de Junção</label>
              <select
                value={joinType}
                onChange={(e) => setJoinType(e.target.value)}
                className={styles.select}
                disabled={!selectedTable}
              >
                <option value="INNER">Registros em ambas</option>
                <option value="LEFT">Todos da principal</option>
              </select>
            </div>
          </div>

          {/* Relacionar por */}
          {selectedTable && (
            <>
              <div className={styles.relationshipBox}>
                <span className={styles.relationshipIcon}>⇄</span>
                <div className={styles.relationshipContent}>
                  <span className={styles.relationshipLabel}>Relacionar por:</span>
                  <span className={styles.relationshipValue}>{getRelationshipLabel()}</span>
                </div>
              </div>

              {/* Campos incluídos automaticamente */}
              <div className={styles.fieldsSection}>
                <h4 className={styles.fieldsSectionTitle}>Campos incluídos automaticamente:</h4>
                <div className={styles.defaultFieldsGrid}>
                  {getDefaultFields().map(fieldValue => {
                    const field = joinFieldsByTable[selectedTable]?.find(f => f.value === fieldValue);
                    return field ? (
                      <label key={field.value} className={styles.fieldCheckbox}>
                        <input
                          type="checkbox"
                          checked={true}
                          disabled={true}
                          className={styles.checkbox}
                        />
                        <span className={styles.checkboxText}>{field.label}</span>
                      </label>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Adicionar mais campos */}
              {getAdditionalFieldOptions().length > 0 && (
                <div className={styles.fieldsSection}>
                  <h4 className={styles.fieldsSectionTitle}>Adicionar mais campos:</h4>
                  <div className={styles.additionalFieldsGrid}>
                    {getAdditionalFieldOptions().map(field => (
                      <label key={field.value} className={styles.fieldCheckbox}>
                        <input
                          type="checkbox"
                          checked={additionalFields.includes(field.value)}
                          onChange={() => handleFieldToggle(field.value)}
                          className={styles.checkbox}
                        />
                        <span className={styles.checkboxText}>{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Rodapé */}
        <div className={styles.modalFooter}>
          <div className={styles.footerLeft}>
            <button 
              className={styles.clearButton} 
              onClick={handleClear}
            >
              ✗ Limpar Junção
            </button>
          </div>
          <div className={styles.footerRight}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancelar
            </button>
            <button 
              className={styles.applyButton} 
              onClick={handleApply}
              disabled={!selectedTable}
            >
              Aplicar Junção
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDom.createPortal(modalContent, document.body);
};

export default JoinTablesModal;

